// Typed client for the /api/* endpoints.
import type {
  BalanceRow,
  Booking,
  BookingStatus,
  CalendarResponse,
  DayResponse,
  Demand,
  DisplayMode,
  JobRole,
  Role,
  Supervisor,
  User,
  UserStatus,
} from './types'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...(options.headers ?? {}) },
      ...options,
    })
  } catch {
    throw new ApiError(0, 'Network error — please check your connection.')
  }
  const text = await res.text()
  const data = text ? JSON.parse(text) : {}
  if (!res.ok) throw new ApiError(res.status, data.error || 'Something went wrong.')
  return data as T
}

const body = (data: unknown) => JSON.stringify(data)

export const api = {
  // auth
  me: () => req<{ user: User }>('/api/auth/me'),
  requestLink: (email: string) =>
    req<{ ok: true; sent: boolean; devLink?: string }>('/api/auth/request-link', {
      method: 'POST',
      body: body({ email }),
    }),
  verifyToken: (token: string) =>
    req<{ ok: true; email: string; name: string; hasPassword: boolean }>('/api/auth/verify', {
      method: 'POST',
      body: body({ token }),
    }),
  setPassword: (token: string, password: string, name: string) =>
    req<{ ok: true; user: User }>('/api/auth/set-password', {
      method: 'POST',
      body: body({ token, password, name }),
    }),
  login: (email: string, password: string) =>
    req<{ ok: true; user: User }>('/api/auth/login', { method: 'POST', body: body({ email, password }) }),
  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  // team
  users: () => req<{ users: User[] }>('/api/users'),
  createUser: (input: {
    email: string
    name: string
    role: Role
    password?: string
    display_mode?: DisplayMode
  }) => req<{ user: User; invited?: boolean }>('/api/users', { method: 'POST', body: body(input) }),
  updateUser: (
    id: string,
    input: { name?: string; role?: Role; status?: UserStatus; password?: string; display_mode?: DisplayMode },
  ) => req<{ user: User }>(`/api/users/${id}`, { method: 'PATCH', body: body(input) }),
  deleteUser: (id: string) => req<{ ok: true }>(`/api/users/${id}`, { method: 'DELETE' }),

  // supervisors + own profile
  supervisors: () => req<{ supervisors: Supervisor[] }>('/api/supervisors'),
  updateProfile: (input: {
    name?: string
    supervisor_id?: string | null
    lang?: 'fi' | 'sv'
    display_mode?: DisplayMode
    password?: string
  }) => req<{ user: User }>('/api/profile', { method: 'PATCH', body: body(input) }),

  // supervisor balance / stats
  stats: (from: string, to: string) =>
    req<{ from: string; to: string; rows: BalanceRow[] }>(`/api/stats?from=${from}&to=${to}`),

  // job roles
  roles: () => req<{ roles: JobRole[] }>('/api/roles'),
  createRole: (input: { name: string; color?: string }) =>
    req<{ role: JobRole }>('/api/roles', { method: 'POST', body: body(input) }),
  deleteRole: (id: string) => req<{ ok: true }>(`/api/roles/${id}`, { method: 'DELETE' }),

  // demands
  demands: (from: string, to: string, roleIds?: string[]) =>
    req<{ demands: Demand[] }>(
      `/api/demands?from=${from}&to=${to}${roleIds && roleIds.length ? `&roles=${roleIds.join(',')}` : ''}`,
    ),
  createDemand: (input: {
    date: string
    start_hour: number
    end_hour: number
    required: number
    role: string
    notes: string
    title?: string
    role_id?: string | null
  }) => req<{ demand: Demand }>('/api/demands', { method: 'POST', body: body(input) }),
  updateDemand: (id: string, input: Partial<Demand>) =>
    req<{ demand: Demand }>(`/api/demands/${id}`, { method: 'PATCH', body: body(input) }),
  deleteDemand: (id: string) => req<{ ok: true }>(`/api/demands/${id}`, { method: 'DELETE' }),

  // bookings
  bookings: (from: string, to: string, scope: 'mine' | 'all' = 'mine') =>
    req<{ bookings: Booking[] }>(`/api/bookings?from=${from}&to=${to}&scope=${scope}`),
  offeredBookings: (from: string, to: string) =>
    req<{ bookings: Booking[] }>(`/api/bookings?from=${from}&to=${to}&offered=1`),
  createBooking: (input: { date: string; hour: number; note?: string; role_id?: string | null }) =>
    req<{ booking: Booking }>('/api/bookings', { method: 'POST', body: body(input) }),
  updateBooking: (id: string, status: BookingStatus) =>
    req<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: body({ status }) }),
  revertBooking: (id: string) =>
    req<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: body({ status: 'pending' }) }),
  offerBooking: (id: string) =>
    req<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: body({ action: 'offer' }) }),
  unofferBooking: (id: string) =>
    req<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: body({ action: 'unoffer' }) }),
  takeBooking: (id: string) =>
    req<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: body({ action: 'take' }) }),
  deleteBooking: (id: string) => req<{ ok: true }>(`/api/bookings/${id}`, { method: 'DELETE' }),

  // calendar
  calendar: (from: string, to: string, roleIds?: string[]) =>
    req<CalendarResponse>(
      `/api/calendar?from=${from}&to=${to}${roleIds && roleIds.length ? `&roles=${roleIds.join(',')}` : ''}`,
    ),
  day: (date: string) => req<DayResponse>(`/api/day?date=${date}`),
}
